import { useMemo } from 'react';
import type { SerializedAssetPublish } from 'fym-shared/src/music24/oneOffPublish';
import { useGetAssetPublishesQuery } from '../../../../../../redux/api/fymAPI';
import type { SerializedKitAsset } from '../../../../../../redux/api/fymAPI.types';
import { useMusic24Capabilities } from '../../../../hooks/useMusic24Capabilities';
import { useVideoCredits } from '../../../credits/useVideoCredits';
import { useRollout } from './RolloutContext';

/** The live record states: a record in one of them holds the channel. */
const LIVE_STATUSES = ['waiting', 'uploading', 'scheduled', 'published'];

export interface MilestoneFacts {
  /** The newest lyric video of the kit (any state), or null. */
  lyricVideo: SerializedKitAsset | null;
  /** The live YouTube record of a lyric video of the song, or null. */
  lyricPublish: SerializedAssetPublish | null;
  canMakeFullVideos: boolean;
  youtubeConnected: boolean;
  /** The ready Canvas loop of the kit, or null. */
  canvas: SerializedKitAsset | null;
  /** The Canvas is marked on Spotify. */
  canvasDone: boolean;
}

function newestOfType(assets: ReadonlyArray<SerializedKitAsset>, themeId: string): SerializedKitAsset | null {
  return (
    [...assets]
      .filter((asset) => asset.themeId === themeId && asset.status !== 'failed')
      .sort((a, b) => b.version - a.version)[0] ?? null
  );
}

/**
 * The facts of the milestones (design rollout-plan a2, b1): the lyric video of the kit and its
 * YouTube record, the full-song video plan gate, the connected YouTube channel, and the Canvas
 * loop and its manual mark (`GET /asset-publishes?catalogItemUuid=`).
 */
export function useMilestoneFacts(): MilestoneFacts {
  const { accountId, item, release, channels } = useRollout();
  const capabilities = useMusic24Capabilities();
  const { data } = useGetAssetPublishesQuery({ accountId, catalogItemUuid: item.uuid });
  const { credits } = useVideoCredits({ accountId });
  const canFull = credits?.canRenderFullSongVideos ?? capabilities.canRenderFullSongVideos;

  return useMemo(() => {
    const assets = [...(release.newest ?? []), ...release.assets];
    const records = data?.publishes ?? [];
    const lyricPublish =
      records
        .filter((record) => record.channel === 'youtube' && record.themeId === 'lyricVideo')
        .filter((record) => LIVE_STATUSES.includes(record.status))
        .sort((a, b) => b.revision - a.revision)[0] ?? null;
    const canvas =
      [...release.assets]
        .filter((asset) => asset.themeId === 'canvasLoop' && asset.status === 'ready')
        .sort((a, b) => b.version - a.version)[0] ?? null;
    return {
      lyricVideo: newestOfType(assets, 'lyricVideo'),
      lyricPublish,
      canMakeFullVideos: canFull,
      youtubeConnected: channels.some((channel) => channel.platform === 'youtube' && channel.status === 'connected'),
      canvas,
      canvasDone: records.some((record) => record.channel === 'spotifyCanvas' && record.status === 'published'),
    };
  }, [canFull, channels, data?.publishes, release]);
}
